import React from 'react';

/**
 * Componente para generar HTML elaborado del reporte de correo
 */
const EmailReportBuilder = {
  /**
   * Genera el HTML completo del reporte
   * @param {Object} data - Datos del reporte
   * @returns {string} HTML del reporte
   */
  buildReportHtml: (data) => {
    const {
      laundryName = '',
      startDate = '',
      endDate = '',
      receptionTotals = {},
      washingTotals = {},
      utilityTotals = {},
      utilityTypeTotals = {},
      workedHours = {},
      productConsumption = [],
      programasLavado = [],
      kilosProcesados = 0
    } = data;

    const fmt = (n) => Number(n || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtInt = (n) => Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 });
    const sDate = String(startDate).slice(0, 10);
    const eDate = String(endDate).slice(0, 10);

    // Cálculos de eficiencia
    const totalConsumoProductos = (Array.isArray(productConsumption) ? productConsumption : [])
      .reduce((a, b) => a + Number(b?.totalUsado || 0), 0);
    const litrosPorKg = totalConsumoProductos / (Number(receptionTotals?.kilosProcesados || 0) || 1);
    const eficienciaHoras = Number(receptionTotals?.kilosProcesados || 0) / (Number(workedHours?.horasAutorizadas || 0) || 1);
    const pctRechazo = (Number(receptionTotals?.kilosRechazo || 0) / (Number(receptionTotals?.kilosProcesados || 0) || 1)) * 100;

    // Costo general por kg
    const costoGeneralPorKg = Number(utilityTotals?.costoTotal || 0) / (Number(receptionTotals?.kilosProcesados || 0) || 1);

    // Top 5 productos por consumo
    const topProductos = (Array.isArray(productConsumption) ? productConsumption : [])
      .sort((a, b) => Number(b?.totalUsado || 0) - Number(a?.totalUsado || 0))
      .slice(0, 5);

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Reporte General Pro-360</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f8fafc; color: #1e293b;">
  <div style="max-width: 800px; margin: 0 auto; background-color: #ffffff; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
    
    ${EmailReportBuilder._renderHeader(laundryName, sDate, eDate)}
    
    <div style="padding: 30px;">
      ${EmailReportBuilder._renderCostPerKg(costoGeneralPorKg, fmt)}
      
      ${EmailReportBuilder._renderMetricsCards(receptionTotals, washingTotals, utilityTotals, workedHours, fmt, fmtInt)}
      
      ${EmailReportBuilder._renderEfficiencyIndicators(litrosPorKg, eficienciaHoras, pctRechazo, costoGeneralPorKg, fmt)}
      
      ${EmailReportBuilder._renderOperationalTable(receptionTotals, washingTotals, utilityTotals, workedHours, totalConsumoProductos, pctRechazo, fmt, fmtInt)}
      
      ${programasLavado.length > 0 ? EmailReportBuilder._renderWashingPrograms(programasLavado, kilosProcesados, fmt, fmtInt) : ''}
      
      ${EmailReportBuilder._renderUtilityServicesBreakdown(utilityTypeTotals, fmt, fmtInt)}
      
      ${topProductos.length > 0 ? EmailReportBuilder._renderTopProducts(topProductos, totalConsumoProductos) : ''}
    </div>

    ${EmailReportBuilder._renderFooter()}
  </div>
</body>
</html>
    `;
  },

  /**
   * Renderiza el header del reporte
   */
  _renderHeader: (laundryName, sDate, eDate) => `
    <div style="background-color: #1e40af; color: white; padding: 30px; text-align: center;">
      <h1 style="margin: 0; font-size: 28px; font-weight: 700; letter-spacing: -0.5px; color: white;">📊 Reporte General Pro-360</h1>
      <div style="margin-top: 15px; padding: 10px 20px; background-color: #3b82f6; border-radius: 8px; display: inline-block;">
        <strong style="font-size: 16px; color: white;">${laundryName}</strong>
      </div>
      <p style="margin: 15px 0 0 0; font-size: 14px; color: white;">
        📅 Período: <strong style="color: white;">${sDate}</strong> al <strong style="color: white;">${eDate}</strong>
      </p>
    </div>
  `,

  /**
   * Renderiza las tarjetas de métricas principales
   */
  _renderMetricsCards: (receptionTotals, washingTotals, utilityTotals, workedHours, fmt, fmtInt) => `
    <h2 style="color: #1e40af; margin: 0 0 20px 0; font-size: 20px; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px;">
      🎯 Indicadores Clave
    </h2>
    
    <div style="background-color: #f1f5f9; padding: 20px; border-radius: 12px; margin-bottom: 30px;">
      <table style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="width: 25%; text-align: center; padding: 10px;">
            <div style="font-size: 24px; font-weight: bold; color: #10b981; margin-bottom: 5px;">${fmt(receptionTotals.kilosProcesados)} kg</div>
            <div style="font-size: 12px; color: #64748b;">Kg Procesados</div>
          </td>
          <td style="width: 25%; text-align: center; padding: 10px;">
            <div style="font-size: 24px; font-weight: bold; color: #6366f1; margin-bottom: 5px;">${fmtInt(washingTotals.ciclos)}</div>
            <div style="font-size: 12px; color: #64748b;">Ciclos de Lavado</div>
          </td>
          <td style="width: 25%; text-align: center; padding: 10px;">
            <div style="font-size: 24px; font-weight: bold; color: #f59e0b; margin-bottom: 5px;">$${fmt(utilityTotals.costoTotal)}</div>
            <div style="font-size: 12px; color: #64748b;">Costo Servicios</div>
          </td>
          <td style="width: 25%; text-align: center; padding: 10px;">
            <div style="font-size: 24px; font-weight: bold; color: #8b5cf6; margin-bottom: 5px;">${fmt(workedHours.horasTrabajadas)} h</div>
            <div style="font-size: 12px; color: #64748b;">Horas Trabajadas</div>
          </td>
        </tr>
      </table>
    </div>
  `,

  /**
   * Renderiza los indicadores de eficiencia
   */
  _renderEfficiencyIndicators: (litrosPorKg, eficienciaHoras, pctRechazo, costoGeneralPorKg, fmt) => `
    <div style="background-color: #f1f5f9; padding: 20px; border-radius: 12px; margin-bottom: 30px;">
      <h3 style="color: #475569; margin: 0 0 15px 0; font-size: 16px;">⚡ Indicadores de Eficiencia</h3>
      <table style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="width: 25%; text-align: center; padding: 10px;">
            <div style="font-size: 18px; font-weight: bold; color: #0ea5e9;">${litrosPorKg.toFixed(3)} L/kg</div>
            <div style="font-size: 12px; color: #64748b;">Consumo por Kilo</div>
          </td>
          <td style="width: 25%; text-align: center; padding: 10px;">
            <div style="font-size: 18px; font-weight: bold; color: #10b981;">${eficienciaHoras.toFixed(2)} kg/h</div>
            <div style="font-size: 12px; color: #64748b;">Kg por Hora Autorizada</div>
          </td>
          <td style="width: 25%; text-align: center; padding: 10px;">
            <div style="font-size: 18px; font-weight: bold; color: ${pctRechazo > 5 ? '#ef4444' : '#10b981'};">${pctRechazo.toFixed(1)}%</div>
            <div style="font-size: 12px; color: #64748b;">% Rechazo</div>
          </td>
          <td style="width: 25%; text-align: center; padding: 10px;">
            <div style="font-size: 18px; font-weight: bold; color: #f59e0b;">$${fmt(costoGeneralPorKg)}</div>
            <div style="font-size: 12px; color: #64748b;">Costo por Kg</div>
          </td>
        </tr>
      </table>
    </div>
  `,

  /**
   * Renderiza la tabla operacional detallada
   */
  _renderOperationalTable: (receptionTotals, washingTotals, utilityTotals, workedHours, totalConsumoProductos, pctRechazo, fmt, fmtInt) => `
    <h2 style="color: #1e40af; margin: 0 0 20px 0; font-size: 20px; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px;">
      📋 Detalle Operacional
    </h2>
    
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px; background-color: white; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
      <thead>
        <tr style="background-color: #f8fafc;">
          <th style="padding: 12px; text-align: left; font-weight: 600; color: #475569; border-bottom: 1px solid #e2e8f0;">Concepto</th>
          <th style="padding: 12px; text-align: right; font-weight: 600; color: #475569; border-bottom: 1px solid #e2e8f0;">Valor</th>
          <th style="padding: 12px; text-align: center; font-weight: 600; color: #475569; border-bottom: 1px solid #e2e8f0;">Estado</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #f1f5f9;">🏭 Recepción - Kg procesados</td>
          <td style="padding: 12px; text-align: right; font-weight: bold; border-bottom: 1px solid #f1f5f9;">${fmt(receptionTotals.kilosProcesados)} kg</td>
          <td style="padding: 12px; text-align: center; border-bottom: 1px solid #f1f5f9;">
            <span style="background-color: #dcfce7; color: #166534; padding: 4px 8px; border-radius: 12px; font-size: 11px;">✓ PROCESADO</span>
          </td>
        </tr>
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #f1f5f9;">❌ Rechazo total</td>
          <td style="padding: 12px; text-align: right; font-weight: bold; border-bottom: 1px solid #f1f5f9;">${fmt(receptionTotals.kilosRechazo)} kg</td>
          <td style="padding: 12px; text-align: center; border-bottom: 1px solid #f1f5f9;">
            <span style="background-color: ${pctRechazo > 5 ? '#fecaca' : '#dcfce7'}; color: ${pctRechazo > 5 ? '#991b1b' : '#166534'}; padding: 4px 8px; border-radius: 12px; font-size: 11px;">
              ${pctRechazo > 5 ? '⚠ ALTO' : '✓ NORMAL'}
            </span>
          </td>
        </tr>
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #f1f5f9;">🔄 Ciclos de lavado</td>
          <td style="padding: 12px; text-align: right; font-weight: bold; border-bottom: 1px solid #f1f5f9;">${fmtInt(washingTotals.ciclos)}</td>
          <td style="padding: 12px; text-align: center; border-bottom: 1px solid #f1f5f9;">
            <span style="background-color: #dbeafe; color: #1e40af; padding: 4px 8px; border-radius: 12px; font-size: 11px;">✓ COMPLETADO</span>
          </td>
        </tr>
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #f1f5f9;">🧺 Kilos lavados</td>
          <td style="padding: 12px; text-align: right; font-weight: bold; border-bottom: 1px solid #f1f5f9;">${fmt(washingTotals.kilosLavados)} kg</td>
          <td style="padding: 12px; text-align: center; border-bottom: 1px solid #f1f5f9;">
            <span style="background-color: #dcfce7; color: #166534; padding: 4px 8px; border-radius: 12px; font-size: 11px;">✓ LAVADO</span>
          </td>
        </tr>
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #f1f5f9;">🧪 Consumo de productos</td>
          <td style="padding: 12px; text-align: right; font-weight: bold; border-bottom: 1px solid #f1f5f9;">${fmt(totalConsumoProductos)} L</td>
          <td style="padding: 12px; text-align: center; border-bottom: 1px solid #f1f5f9;">
            <span style="background-color: #fef3c7; color: #92400e; padding: 4px 8px; border-radius: 12px; font-size: 11px;">📊 MEDIDO</span>
          </td>
        </tr>
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #f1f5f9;">⏰ Horas autorizadas</td>
          <td style="padding: 12px; text-align: right; font-weight: bold; border-bottom: 1px solid #f1f5f9;">${fmt(workedHours.horasAutorizadas)} h</td>
          <td style="padding: 12px; text-align: center; border-bottom: 1px solid #f1f5f9;">
            <span style="background-color: #e0e7ff; color: #3730a3; padding: 4px 8px; border-radius: 12px; font-size: 11px;">✓ AUTORIZADO</span>
          </td>
        </tr>
      </tbody>
    </table>
  `,

  /**
   * Renderiza el ranking de productos
   */
  _renderTopProducts: (topProductos, totalConsumoProductos) => `
    <h2 style="color: #1e40af; margin: 0 0 20px 0; font-size: 20px; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px;">
      🏆 Top Productos por Consumo
    </h2>
    
    <div style="background-color: #f8fafc; padding: 20px; border-radius: 12px; margin-bottom: 30px;">
      ${topProductos.map((p, idx) => {
    const litros = Number(p?.totalUsado || 0);
    const pct = totalConsumoProductos > 0 ? (litros / totalConsumoProductos) * 100 : 0;
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
    return `
          <div style="display: flex; align-items: center; margin-bottom: ${idx < topProductos.length - 1 ? '15px' : '0'};">
            <div style="width: 30px; height: 30px; background-color: ${colors[idx]}; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; margin-right: 15px;">
              ${idx + 1}
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 600; color: #1e293b; margin-bottom: 2px;">${p?.producto?.nombre || 'Sin nombre'}</div>
              <div style="background-color: #e2e8f0; height: 8px; border-radius: 4px; overflow: hidden;">
                <div style="background-color: ${colors[idx]}; height: 100%; width: ${pct}%; transition: width 0.3s ease;"></div>
              </div>
            </div>
            <div style="margin-left: 15px; text-align: right;">
              <div style="font-weight: bold; color: #1e293b;">${litros.toFixed(1)} L</div>
              <div style="font-size: 12px; color: #64748b;">${pct.toFixed(1)}%</div>
            </div>
          </div>
        `;
  }).join('')}
    </div>
  `,

  /**
   * Renderiza el costo general por kg
   */
  _renderCostPerKg: (costoGeneralPorKg, fmt) => `
    <div style="background-color: #f1f5f9; padding: 25px; border-radius: 12px; margin-bottom: 30px; text-align: center;">
      <div style="font-size: 14px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px; color: #64748b; font-weight: 600;">💰 Costo General por Kilogramo</div>
      <div style="font-size: 36px; font-weight: bold; margin-bottom: 5px; color: #dc2626;">$${fmt(costoGeneralPorKg)}</div>
      <div style="font-size: 13px; color: #64748b;">Costo de servicios públicos por kg procesado</div>
    </div>
  `,

  /**
   * Renderiza la participación por programas de lavado
   */
  _renderWashingPrograms: (programasLavado, kilosProcesados, fmt, fmtInt) => `
    <h2 style="color: #1e40af; margin: 30px 0 20px 0; font-size: 20px; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px;">
      🔄 Programas de Lavado
    </h2>
    
    <div style="background-color: #f8fafc; padding: 20px; border-radius: 12px; margin-bottom: 30px;">
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr style="background-color: #e2e8f0;">
            <th style="padding: 10px; text-align: left; font-weight: 600; color: #475569; border-bottom: 2px solid #cbd5e1;">Programa</th>
            <th style="padding: 10px; text-align: right; font-weight: 600; color: #475569; border-bottom: 2px solid #cbd5e1;">Kilos</th>
            <th style="padding: 10px; text-align: right; font-weight: 600; color: #475569; border-bottom: 2px solid #cbd5e1;">Ciclos</th>
            <th style="padding: 10px; text-align: right; font-weight: 600; color: #475569; border-bottom: 2px solid #cbd5e1;">% Participación</th>
          </tr>
        </thead>
        <tbody>
          ${programasLavado.map((prog, idx) => {
    const pct = kilosProcesados > 0 ? (Number(prog?.kilos || 0) / kilosProcesados) * 100 : 0;
    return `
              <tr style="${idx % 2 === 0 ? 'background-color: white;' : 'background-color: #f8fafc;'}">
                <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; color: #1e293b; font-weight: 500;">${prog?.nombre || 'Sin programa'}</td>
                <td style="padding: 10px; text-align: right; border-bottom: 1px solid #e2e8f0; color: #1e293b;">${fmt(prog?.kilos || 0)} kg</td>
                <td style="padding: 10px; text-align: right; border-bottom: 1px solid #e2e8f0; color: #1e293b;">${fmtInt(prog?.count || 0)}</td>
                <td style="padding: 10px; text-align: right; border-bottom: 1px solid #e2e8f0;">
                  <div style="display: flex; align-items: center; justify-content: flex-end; gap: 8px;">
                    <div style="background-color: #e2e8f0; height: 8px; width: 60px; border-radius: 4px; overflow: hidden;">
                      <div style="background-color: #3b82f6; height: 100%; width: ${pct}%;"></div>
                    </div>
                    <span style="font-weight: 600; color: #1e293b;">${pct.toFixed(1)}%</span>
                  </div>
                </td>
              </tr>
            `;
  }).join('')}
        </tbody>
      </table>
    </div>
  `,

  /**
   * Renderiza el desglose de servicios públicos
   */
  _renderUtilityServicesBreakdown: (utilityTypeTotals, fmt, fmtInt) => `
    <h2 style="color: #1e40af; margin: 30px 0 20px 0; font-size: 20px; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px;">
      💡 Desglose de Servicios Públicos
    </h2>
    
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px;">
      <tr>
        <!-- Agua -->
        <td style="width: 25%; padding: 5px;">
          <div style="background-color: #0ea5e9; color: white; padding: 20px; border-radius: 8px; text-align: center;">
            <div style="font-size: 12px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">💧 Agua</div>
            <div style="font-size: 24px; font-weight: bold; margin-bottom: 4px;">${fmt(utilityTypeTotals?.agua?.consumoLitros || 0)} L</div>
            <div style="font-size: 18px; font-weight: 600;">$${fmt(utilityTypeTotals?.agua?.costos || 0)}</div>
          </div>
        </td>
        
        <!-- Gas -->
        <td style="width: 25%; padding: 5px;">
          <div style="background-color: #f59e0b; color: white; padding: 20px; border-radius: 8px; text-align: center;">
            <div style="font-size: 12px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">🔥 Gas</div>
            <div style="font-size: 24px; font-weight: bold; margin-bottom: 4px;">${fmt(utilityTypeTotals?.gas?.consumoLitros || 0)} L</div>
            <div style="font-size: 18px; font-weight: 600;">$${fmt(utilityTypeTotals?.gas?.costos || 0)}</div>
          </div>
        </td>
        
        <!-- Electricidad -->
        <td style="width: 25%; padding: 5px;">
          <div style="background-color: #a21caf; color: white; padding: 20px; border-radius: 8px; text-align: center;">
            <div style="font-size: 12px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">⚡ Electricidad</div>
            <div style="font-size: 24px; font-weight: bold; margin-bottom: 4px;">${fmt(utilityTypeTotals?.Electricidad?.consumoLitros || 0)} kWh</div>
            <div style="font-size: 18px; font-weight: 600;">$${fmt(utilityTypeTotals?.Electricidad?.costos || 0)}</div>
          </div>
        </td>
        
        <!-- Agua Caliente -->
        <td style="width: 25%; padding: 5px;">
          <div style="background-color: #06b6d4; color: white; padding: 20px; border-radius: 8px; text-align: center;">
            <div style="font-size: 12px; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">🌡️ Agua Caliente</div>
            <div style="font-size: 24px; font-weight: bold; margin-bottom: 4px;">${fmt(utilityTypeTotals?.aguaCaliente?.consumoLitros || 0)} L</div>
            <div style="font-size: 18px; font-weight: 600;">$${fmt(utilityTypeTotals?.aguaCaliente?.costos || 0)}</div>
          </div>
        </td>
      </tr>
    </table>
  `,

  /**
   * Renderiza el footer del reporte
   */
  _renderFooter: () => `
    <div style="background-color: #1e293b; color: white; padding: 20px; text-align: center;">
      <p style="margin: 0; font-size: 14px; opacity: 0.8;">
        📧 Reporte generado automáticamente por <strong>ERP Pro-360</strong>
      </p>
      <p style="margin: 5px 0 0 0; font-size: 12px; opacity: 0.6;">
        ${new Date().toLocaleDateString('es-CO', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}
      </p>
    </div>
  `
};

export default EmailReportBuilder;
